import { joostElementSliceType, normaliseJoostElementIdentifier, type JoostElementIdentifier } from '@/composables/product-page/joostElements';
import type { AppPageProps } from '@/types';
import { usePage } from '@inertiajs/vue3';
import _ from 'lodash';
import { computed, type ComputedRef } from 'vue';

/**
 * Narrow structural type on purpose: the generated ProductPage.body is a union-of-arrays that does
 * not narrow usefully, and we only ever read two fields here.
 */
type PrismicBodySlice = {
    slice_type: string;
    primary?: { element?: string | null } | null;
};

type PrismicPageProp = { body?: PrismicBodySlice[] | null } | null;

type UseJoostElements = {
    placedIdentifiers: ComputedRef<Set<JoostElementIdentifier>>;
    isPlacedInPrismic: (identifier: JoostElementIdentifier) => boolean;
};

/**
 * Reports which Joost sections the editor positioned inside the Prismic body, so the product page
 * can skip them at their hardcoded position. Without a Prismic page the set is empty and the page
 * renders exactly as it did before this feature existed.
 */
export function useJoostElements(): UseJoostElements {
    const page = usePage<AppPageProps & { prismic_page?: PrismicPageProp }>();

    const placedIdentifiers = computed((): Set<JoostElementIdentifier> => {
        const body = page.props.prismic_page?.body ?? [];

        const identifiers = _.filter(body, (slice: PrismicBodySlice): boolean => slice.slice_type === joostElementSliceType).map(
            (slice: PrismicBodySlice): JoostElementIdentifier | undefined => normaliseJoostElementIdentifier(slice.primary?.element),
        );

        return new Set(identifiers.filter((identifier): identifier is JoostElementIdentifier => identifier !== undefined));
    });

    const isPlacedInPrismic = (identifier: JoostElementIdentifier): boolean => placedIdentifiers.value.has(identifier);

    return { placedIdentifiers, isPlacedInPrismic };
}
